import React, { useState, useEffect } from "react"
import i18n from "i18n"
import Dialog from "@mui/material/Dialog"
import CircularProgress from "@mui/material/CircularProgress"
import CloseIcon from "@mui/icons-material/Close"
import PropTypes from "prop-types"

import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"

import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  DialogTitle,
  IconButton,
} from "@mui/material"
import {
  DEFAULT_DECIMALS,
  UNITS,
  TOTALS,
  MARGINS,
  REDUCTIONS,
  DIMENSIONS,
  MEASUREMENTS,
  SUB_DECOMPOSITIONS,
  TOTAL_DECOMPOSITIONS,
  COEFFICIENT_DECIMALS_CAP,
} from "constants/decimals"
import IntegerCoefficientFormat from "common/IntegerCoefficientFormat"

const DECIMAL_SETTINGS_FIELDS = [
  { key: UNITS, labelKey: "editProject.units" },
  { key: TOTALS, labelKey: "editProject.totals" },
  { key: MARGINS, labelKey: "editProject.margins" },
  { key: REDUCTIONS, labelKey: "editProject.reductions" },
  { key: MEASUREMENTS, labelKey: "editProject.measurements" },
  { key: DIMENSIONS, labelKey: "editProject.dimensions" },
  { key: SUB_DECOMPOSITIONS, labelKey: "editProject.subDecompositions" },
  { key: TOTAL_DECOMPOSITIONS, labelKey: "editProject.totalDecompositions" },
]

const displayCoefficientValue = v => {
  if (v === "" || v === undefined || v === null) return ""
  if (typeof v === "number" && !Number.isFinite(v)) return ""
  return v
}

const ModalDecimalsSettings = ({
  open,
  onClose,
  onSave,
  loading,
  coefficients,
}) => {
  const [localLoading, setLocalLoading] = useState(false)
  useEffect(() => {
    if (typeof loading !== "undefined") setLocalLoading(loading)
  }, [loading])

  const [values, setValues] = useState({ ...DEFAULT_DECIMALS })

  const handleCoefficientChange = key => e => {
    const raw = e.target.value
    if (raw === "" || raw === undefined || raw === null) {
      setValues(prev => ({ ...prev, [key]: "" }))
      return
    }
    const num = Number(raw)
    if (!Number.isFinite(num)) {
      return
    }
    setValues(prev => ({ ...prev, [key]: num }))
  }

  const handleBlur = key => () => {
    setValues(prev => {
      const curr = prev?.[key]
      if (
        curr === "" ||
        curr === undefined ||
        curr === null ||
        (typeof curr === "number" && !Number.isFinite(curr))
      ) {
        return { ...prev, [key]: 0 }
      }
      if (typeof curr === "number" && curr > COEFFICIENT_DECIMALS_CAP) {
        return { ...prev, [key]: COEFFICIENT_DECIMALS_CAP }
      }
      return prev
    })
  }

  const resolveValue = key => {
    const val =
      coefficients && typeof coefficients[key] !== "undefined"
        ? coefficients[key]
        : undefined
    const resolved = val == null ? DEFAULT_DECIMALS[key] : val
    if (typeof resolved === "number" && !Number.isFinite(resolved)) {
      return DEFAULT_DECIMALS[key]
    }
    return resolved
  }

  useEffect(() => {
    if (!open) return
    try {
      const resolved = {}
      DECIMAL_SETTINGS_FIELDS.forEach(({ key }) => {
        resolved[key] = resolveValue(key)
      })

      setValues(prev => ({ ...prev, ...resolved }))
    } catch (e) {
      console.error("debug log error:", e)
    }
  }, [open, coefficients])

  const handleLocalSave = async () => {
    try {
      if (onSave) await onSave(values)
    } catch (e) {
      console.error("ModalDecimalsSettings.handleLocalSave - save error:", e)
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onExited: () => setValues({ ...DEFAULT_DECIMALS }),
        }}
      >
        <DialogTitle>
          {i18n.t("decimalsSettings.decimals")}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {i18n.t("decimalsSettings.setDecimalsInfo")}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {DECIMAL_SETTINGS_FIELDS.map(({ key, labelKey }) => {
              const v = values[key]
              const overMax =
                typeof v === "number" &&
                Number.isFinite(v) &&
                v > COEFFICIENT_DECIMALS_CAP
              return (
                <Grid item xs={6} sm={4} md={4} key={key}>
                  <TextField
                    label={i18n.t(labelKey)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    margin="normal"
                    name={key}
                    value={displayCoefficientValue(v)}
                    InputProps={{
                      inputComponent: IntegerCoefficientFormat,
                    }}
                    onChange={handleCoefficientChange(key)}
                    onBlur={handleBlur(key)}
                    error={overMax}
                    helperText={
                      overMax
                        ? i18n.t("decimalsSettings.maxDecimals", {
                            max: COEFFICIENT_DECIMALS_CAP,
                          })
                        : undefined
                    }
                    disabled={false}
                  />
                </Grid>
              )
            })}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onClose}
            color="branded"
            variant="outlined"
            disabled={localLoading}
          >
            {i18n.t("numerationSettings.cancel")}
          </Button>
          <Button
            onClick={handleLocalSave}
            color="branded"
            variant="contained"
            disabled={localLoading}
            sx={{ width: "106px", height: "36px" }}
          >
            {localLoading ? (
              <CircularProgress size={24} style={{ color: "white" }} />
            ) : (
              i18n.t("numerationSettings.save")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

ModalDecimalsSettings.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  loading: PropTypes.bool,
  coefficients: PropTypes.object,
}

ModalDecimalsSettings.defaultProps = {
  onSave: () => {},
}

export default ModalDecimalsSettings
